/**
 * Client-side hydration entry for SSR'd Stippl marketing & blog pages
 * (stippl.io / business.stippl.io and their previews).
 *
 * The admin SPA entry (`src/index.tsx`) and the tenant SSR entry
 * (`entry-client.tsx`) are unchanged. This entry only fires on pages
 * that were rendered by `renderMarketing()` server-side and includes
 * a serialised `__MARKETING_SSR_DATA__` blob.
 */

import { ApolloProvider } from "@apollo/client";
import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MarketingShell } from "./components/website/marketing/MarketingShell";
import { preloadMarketingPage, type MarketingPageId } from "./components/website/marketing/marketingPages";
import type { HostFlavor } from "./components/website/marketing/marketingRoutes";
import { captureFirstTouch } from "./utils/acquisition";
import { rememberOpenAIClick } from "./utils/openaiPixel";
import { BusinessProvider } from "./context/BusinessContext";
import { client } from "./graphql/client";
// See css/_marketingReset.scss for why this isn't just `import "./css/General.scss"`.
import "./css/_marketingReset.scss";
// Global stylesheets that one page imports and other pages silently depend on. Before the route
// split every page's CSS sat in one file, so these reached every page; now each page only gets the
// CSS of its own chunk, so they are loaded here, always. Found by testing, on the live DOM of each
// marketing page, every selector from the CSS files that page no longer loads:
//   - PrimaryButton.scss: `.btn-rounded`, used in the nav and CTAs of nearly every page;
//   - ItineraryPlanner.scss: the `.hr-*` hero shared by every feature page (AI, budget, packing, eSIM…);
//   - business marketing.css: `.bg-white` / `.text-center` utilities used on feature and pricing pages;
//   - Blog.scss: `#stippl-blog`, which the /blog index needs but only the article page imports.
// Order matters: Buttons.scss before PrimaryButton.scss, as in the single pre-split stylesheet. Both
// style `.btn-rounded.xl` (600, then 700). Left to the shared Footer chunk, Buttons.scss loaded after
// the entry and its 600 won on every solid CTA; the Footer's identical copy is then removed by the
// dedupe step in vite.config.ssr.ts.
import "./css/Buttons.scss";
import "./components/buttons/PrimaryButton.scss";
import "./components/website/css/ItineraryPlanner.scss";
import "./components/website/content/business/homepage/styles/marketing.css";
import "./components/website/css/Blog.scss";

interface MarketingSsrData {
	hostFlavor: HostFlavor;
	pathname: string;
	data?: any;
	lang?: string;
	/** Which page the server matched — the chunk to preload before the first hydration render. */
	page?: MarketingPageId;
}

captureFirstTouch(); // first-touch acquisition attribution (marketing/paid landings)
rememberOpenAIClick(); // ChatGPT ad click id, handed to the pixel after consent in the app

const ssrData: MarketingSsrData | undefined = (window as any).__MARKETING_SSR_DATA__;

/**
 * Bootstraps hydration. Wrapped in an async function so the matched page's chunk can be awaited
 * BEFORE the first client render: `MarketingShell` renders synchronously off `getResolvedMarketingPage`,
 * and the server render (`renderToString`, no `React.lazy`) already produced that page's real markup —
 * so the first client render has to resolve the same component synchronously too, or it mismatches
 * the SSR DOM `hydrateRoot` is reconciling against. See `marketingPages.ts` and `MarketingShell.tsx`.
 */
async function bootstrap(): Promise<void> {
	if (!ssrData) return;
	const container = document.getElementById("ssr-root");
	if (!container) return;

	if (ssrData.page) {
		try {
			await preloadMarketingPage(ssrData.page);
		} catch (e: any) {
			// Not fatal: MarketingShell falls back to its own <Suspense>-wrapped lazy load for
			// this route, so the page still renders — just not on the very first paint.
			console.error("[MarketingSSR] Failed to preload matched page before hydration:", { page: ssrData.page, error: e?.message || e });
		}
	} else {
		console.error("[MarketingSSR] SSR data has no page id — falling back to lazy resolution:", { pathname: ssrData.pathname });
	}

	// hydrateRoot: reconciles the SSR DOM with the React tree instead of replacing
	// it. This eliminates the ~1.0 CLS score caused by createRoot wiping the entire
	// SSR DOM and mounting a fresh tree (which the browser measures as a full-page
	// layout shift).
	//
	// Prerequisites for hydration to work:
	//   1. AnimatePresence/motion.* in HomeMarketing and ProductBenefits are gated
	//      behind `mounted` — plain divs on SSR/initial render, animated after
	//      useEffect. This ensures SSR and initial client output match.
	//   2. DropdownMenu/FeatureMenu use `initial={false}` + explicit variant values,
	//      which framer-motion renders consistently on both server and client.
	const app = (
		<ApolloProvider client={client}>
			<BusinessProvider>
				<BrowserRouter>
					<MarketingShell hostFlavor={ssrData.hostFlavor} data={ssrData.data} initialPath={ssrData.pathname} />
				</BrowserRouter>
			</BusinessProvider>
		</ApolloProvider>
	);

	// In dev mode the SSR outlet is empty (no server-rendered HTML) —
	// use createRoot for a clean CSR mount instead of a mismatched hydration.
	// firstElementChild ignores comment/text nodes (e.g. the dev-mode <!-- dev-csr -->
	// placeholder), so CSR is used when there's no real SSR HTML in the outlet.
	if (container.firstElementChild) {
		hydrateRoot(container, app);
	} else {
		createRoot(container).render(app);
	}
}

bootstrap();
